import { useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import {
    DUE_DATE_STATUSES,
    getDueDateStatusFromDate,
} from "../../helpers/dueDateStatus";
import { getDueTodayTextClass } from "../../helpers/dueTodayAppearance";
import { getDueDateReport } from "../../services/analytics/getDueDateReport";
import { getDueDateReportServices } from "../../services/analytics/getDueDateReportServices";
import { getEmployees } from "../../services/getEmployees";
import { getServiceDepartments } from "../../services/service/getServiceDepartments";

const DEFAULT_OPTION = {
    value: "00000000-0000-0000-0000-000000000000",
    label: "All",
    is_showing: true,
};

export const DUE_DATE_REPORT_ITEMS = [
    {
        value: 1,
        label: "Services",
        is_showing: true,
    },
    {
        value: 2,
        label: "Steps",
        is_showing: true,
    },
];

const ALLOWED_DUE_DATE_ALIASES = ["pastDue", "next7days", "thereafter"];

const createDueDateStatuses = () =>
    DUE_DATE_STATUSES.filter((status) =>
        ALLOWED_DUE_DATE_ALIASES.includes(status.alias),
    ).map((status) => ({
        ...status,
        numberOfJobs: 0,
        numberOfServices: 0,
        jobs: [],
        services: [],
        dueTodayCount: 0,
        dueTodayJobs: [],
        dueTodayServices: [],
    }));

const getDueTodayCount = (report) =>
    report?.dueTodayCount ??
    report?.numberDueToday ??
    report?.today ??
    report?.numberOfToday ??
    0;

const normalizeDate = (value) => {
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return null;
    return new Date(date.getFullYear(), date.getMonth(), date.getDate());
};

const isTodayLocal = (value) => {
    const date = normalizeDate(value);
    if (!date) return false;

    const today = normalizeDate(new Date());
    return today ? date.getTime() === today.getTime() : false;
};

const getDueTodayJobs = (report) =>
    report?.dueTodayJobs ??
    report?.jobs?.filter((job) => isTodayLocal(job?.dueDate)) ??
    [];

const getDueTodayServices = (report) =>
    report?.dueTodayServices ??
    report?.services?.filter((service) =>
        isTodayLocal(service?.subscriptionEndDate),
    ) ??
    [];

const getInitialSelectedItem = (selectedItem, reportItems = DUE_DATE_REPORT_ITEMS) =>
    selectedItem || reportItems[0] || DUE_DATE_REPORT_ITEMS[0];

const isDateWithinLastSevenDays = (value) => {
    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
        return false;
    }

    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    return date >= sevenDaysAgo && date <= today;
};

const getNotesValue = (...candidates) => {
    const visited = new WeakSet();

    const findNotes = (candidate, depth = 0) => {
        if (candidate == null) return "";

        if (typeof candidate === "string") {
            return candidate;
        }

        if (typeof candidate !== "object") {
            return "";
        }

        if (visited.has(candidate) || depth > 2) {
            return "";
        }

        visited.add(candidate);

        if (typeof candidate.notes === "string") {
            return candidate.notes;
        }

        if (typeof candidate.note === "string") {
            return candidate.note;
        }

        if (candidate.notesAndMentions) {
            const nestedNotes = findNotes(candidate.notesAndMentions, depth + 1);
            if (nestedNotes) {
                return nestedNotes;
            }
        }

        if (candidate.clientServiceSubscriptionTask) {
            const nestedNotes = findNotes(
                candidate.clientServiceSubscriptionTask,
                depth + 1,
            );
            if (nestedNotes) {
                return nestedNotes;
            }
        }

        if (candidate.clientServiceSubscription) {
            const nestedNotes = findNotes(
                candidate.clientServiceSubscription,
                depth + 1,
            );
            if (nestedNotes) {
                return nestedNotes;
            }
        }

        return "";
    };

    for (const candidate of candidates) {
        const noteValue = findNotes(candidate);
        if (noteValue) {
            return noteValue;
        }
    }

    return "";
};

const DueDateSummarySection = ({
    employeeId = null,
    title = "Due Date Report",
    selectedItem: controlledSelectedItem,
    onSelectedItemChange,
    onSelectionChange,
    selectedBucketAlias = null,
    onSelectedBucketAliasChange,
    showEmployeeSelector = true,
    showDepartmentSelector = true,
    showAssignmentSelector = true,
    showClearSelection = false,
    clearSelectionLabel = "Clear filter",
    reportItems = DUE_DATE_REPORT_ITEMS,
    loadReportData,
    sectionClassName = "mt-10",
    onLoadingChange,
    onError,
}) => {
    const activeRequestRef = useRef(0);
    const onErrorRef = useRef(onError);
    const onLoadingChangeRef = useRef(onLoadingChange);
    const onSelectionChangeRef = useRef(onSelectionChange);
    const onSelectedItemChangeRef = useRef(onSelectedItemChange);
    const onSelectedBucketAliasChangeRef = useRef(onSelectedBucketAliasChange);
    const [internalSelectedItem, setInternalSelectedItem] = useState(() =>
        getInitialSelectedItem(controlledSelectedItem, reportItems),
    );
    const [dueDateReports, setDueDateReports] = useState(createDueDateStatuses());
    const [reportError, setReportError] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [employees, setEmployees] = useState([DEFAULT_OPTION]);
    const [departments, setDepartments] = useState([DEFAULT_OPTION]);
    const [selectedEmployee, setSelectedEmployee] = useState(DEFAULT_OPTION);
    const [selectedDepartment, setSelectedDepartment] = useState(DEFAULT_OPTION);

    const selectedItem = controlledSelectedItem ?? internalSelectedItem;
    const selectedItemLabel = selectedItem?.label ?? "Services";
    const effectiveEmployeeId = showEmployeeSelector
        ? selectedEmployee?.value
        : employeeId;

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    useEffect(() => {
        onLoadingChangeRef.current = onLoadingChange;
    }, [onLoadingChange]);

    useEffect(() => {
        onSelectionChangeRef.current = onSelectionChange;
    }, [onSelectionChange]);

    useEffect(() => {
        onSelectedItemChangeRef.current = onSelectedItemChange;
    }, [onSelectedItemChange]);

    useEffect(() => {
        onSelectedBucketAliasChangeRef.current = onSelectedBucketAliasChange;
    }, [onSelectedBucketAliasChange]);

    useEffect(() => {
        if (controlledSelectedItem !== undefined) return;
        setInternalSelectedItem((currentSelectedItem) => {
            if (
                currentSelectedItem &&
                reportItems.some((item) => item.value === currentSelectedItem.value)
            ) {
                return currentSelectedItem;
            }

            return getInitialSelectedItem(null, reportItems);
        });
    }, [controlledSelectedItem, reportItems]);

    const setLoadingState = (nextValue) => {
        setIsLoading(nextValue);
        onLoadingChangeRef.current?.(nextValue);
    };

    useEffect(() => {
        let isMounted = true;

        async function fetchDepartments() {
            try {
                if (!showDepartmentSelector) {
                    if (isMounted) {
                        setDepartments([DEFAULT_OPTION]);
                    }
                    return;
                }

                const response = await getServiceDepartments();
                if (!response?.success || !response?.data?.success) {
                    throw new Error(
                        response?.message || "Unable to load departments.",
                    );
                }
                if (!isMounted) return;

                const options = response.data.departments.map((department) => ({
                    value: department.id,
                    label: department.name,
                    is_showing: true,
                }));
                setDepartments([DEFAULT_OPTION, ...options]);
            } catch (error) {
                if (!isMounted) return;
                setDepartments([DEFAULT_OPTION]);
                onErrorRef.current?.(error);
            }
        }

        fetchDepartments();

        return () => {
            isMounted = false;
        };
    }, [showDepartmentSelector]);

    useEffect(() => {
        let isMounted = true;

        async function fetchEmployees() {
            try {
                if (!showEmployeeSelector) {
                    if (isMounted) {
                        setEmployees([DEFAULT_OPTION]);
                    }
                    return;
                }

                const response = await getEmployees();
                const data = response?.data?.employees;

                if (!response?.success || !Array.isArray(data)) {
                    throw new Error(
                        response?.message || "Unable to load employees.",
                    );
                }
                if (!isMounted || !Array.isArray(data)) return;

                const options = data.map((employee) => ({
                    value: employee.employeeId,
                    label: `${employee.firstName} ${employee.lastName}`,
                    is_showing: true,
                }));
                setEmployees([DEFAULT_OPTION, ...options]);
            } catch (error) {
                if (!isMounted) return;
                setEmployees([DEFAULT_OPTION]);
                onErrorRef.current?.(error);
            }
        }

        fetchEmployees();

        return () => {
            isMounted = false;
        };
    }, [showEmployeeSelector]);

    useEffect(() => {
        let isMounted = true;

        async function loadReport() {
            const requestId = activeRequestRef.current + 1;
            activeRequestRef.current = requestId;
            setLoadingState(true);
            setReportError(null);
            onErrorRef.current?.(null);

            try {
                if (!selectedItemLabel) {
                    if (isMounted) {
                        setDueDateReports(createDueDateStatuses());
                    }
                    return;
                }

                if (
                    !showEmployeeSelector &&
                    !effectiveEmployeeId &&
                    typeof loadReportData !== "function"
                ) {
                    if (isMounted) {
                        setDueDateReports(createDueDateStatuses());
                    }
                    return;
                }

                const nextReports = [];
                for (const report of createDueDateStatuses()) {
                        if (typeof loadReportData === "function") {
                            const customReport = await loadReportData({
                                reportAlias: report.alias,
                                selectedItem,
                                selectedItemLabel,
                                employeeId: effectiveEmployeeId,
                                departmentId: selectedDepartment.value,
                            });

                            nextReports.push({
                                ...report,
                                ...(customReport || {}),
                            });
                            continue;
                        }

                        const request = {
                            rangeName: report.alias,
                            departmentId: selectedDepartment.value,
                            includeInactive: false,
                        };

                        if (effectiveEmployeeId) {
                            request.employeeId = effectiveEmployeeId;
                        }

                        const response =
                            selectedItemLabel === "Steps"
                                ? await getDueDateReport(request)
                                : await getDueDateReportServices(request);

                        const apiSuccess =
                            response?.data?.success ?? response?.success ?? false;
                        const apiReport =
                            response?.data?.report ?? response?.report ?? null;

                        if (!apiSuccess || !apiReport) {
                            throw new Error(
                                response?.message ||
                                    `Unable to load ${selectedItemLabel.toLowerCase()} due date report.`,
                            );
                        }

                        if (selectedItemLabel === "Steps") {
                            nextReports.push({
                                ...report,
                                numberOfJobs: apiReport.numberOfJobs ?? 0,
                                jobs: apiReport.jobs || [],
                                dueTodayCount:
                                    report.alias === "next7days"
                                        ? getDueTodayCount(apiReport)
                                        : 0,
                                dueTodayJobs:
                                    report.alias === "next7days"
                                        ? getDueTodayJobs(apiReport)
                                        : [],
                            });
                            continue;
                        }

                        nextReports.push({
                            ...report,
                            numberOfServices: apiReport.numberOfServices ?? 0,
                            services: apiReport.services || [],
                            dueTodayCount:
                                report.alias === "next7days"
                                    ? getDueTodayCount(apiReport)
                                    : 0,
                            dueTodayServices:
                                report.alias === "next7days"
                                    ? getDueTodayServices(apiReport)
                                    : [],
                        });
                }

                if (!isMounted) return;
                setDueDateReports(nextReports);
            } catch (err) {
                if (!isMounted) return;
                setReportError(err);
                onErrorRef.current?.(err);
                setDueDateReports(createDueDateStatuses());
            } finally {
                const isLatestRequest = requestId === activeRequestRef.current;
                if (isLatestRequest && isMounted) {
                    setIsLoading(false);
                }
                if (isLatestRequest) {
                    onLoadingChangeRef.current?.(false);
                }
            }
        }

        loadReport();

        return () => {
            isMounted = false;
        };
    }, [
        effectiveEmployeeId,
        loadReportData,
        selectedDepartment.value,
        selectedItemLabel,
        showEmployeeSelector,
    ]);

    const itemOptions = useMemo(() => reportItems, [reportItems]);

    const handleItemChange = (item) => {
        if (controlledSelectedItem === undefined) {
            setInternalSelectedItem(item);
        }
        onSelectedItemChangeRef.current?.(item);
    };

    const handleCardSelection = (rows, reportName, reportAlias) => {
        const nextBucketAlias =
            selectedBucketAlias === reportAlias ? null : reportAlias;

        onSelectedBucketAliasChangeRef.current?.(nextBucketAlias);
        onSelectionChangeRef.current?.({
            rows: nextBucketAlias ? rows : [],
            reportName: nextBucketAlias ? reportName : null,
            reportAlias: nextBucketAlias,
            selectedItem,
            isCleared: nextBucketAlias === null,
        });
    };

    const renderReportCountButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className="mt-4 font-sans text-white transition-transform duration-150 cursor-pointer text-7xl hover:scale-105 hover:text-slate-200"
        >
            {count}
        </button>
    );

    const renderDueTodayButton = (count, onClick) => (
        <button
            type="button"
            onClick={onClick}
            className={`mt-3 text-base font-semibold transition-transform duration-150 cursor-pointer hover:scale-105 ${getDueTodayTextClass({
                surface: "warning",
                interactive: true,
            })}`}
        >
            Due today: {count}
        </button>
    );

    return (
        <section className={sectionClassName}>
            <div className="mb-8 sm:flex sm:justify-between sm:items-center">
                <div className="mb-4 sm:mb-0">
                    <h2 className="text-2xl font-bold text-slate-800">
                        {title}
                    </h2>
                </div>

                <div className="flex flex-col items-center gap-3 sm:flex-row sm:gap-2">
                    {showAssignmentSelector && (
                        <div className="flex flex-col items-start">
                            <h3 className="font-bold text-slate-800">
                                Select Assignment
                            </h3>
                            <Select
                                value={selectedItem}
                                onChange={handleItemChange}
                                options={itemOptions}
                                className="basic-single min-w-[180px] w-auto"
                            />
                        </div>
                    )}

                    {showEmployeeSelector && (
                        <div className="flex flex-col items-start">
                            <h3 className="font-bold text-slate-800">
                                Select Employee
                            </h3>
                            <Select
                                value={selectedEmployee}
                                onChange={setSelectedEmployee}
                                options={employees}
                                isSearchable
                                className="basic-single min-w-[180px] w-auto"
                            />
                        </div>
                    )}

                    {showDepartmentSelector && (
                        <div className="flex flex-col items-start">
                            <h3 className="font-bold text-slate-800">
                                Select Department
                            </h3>
                            <Select
                                value={selectedDepartment}
                                onChange={setSelectedDepartment}
                                options={departments}
                                className="basic-single min-w-[180px] w-auto"
                            />
                        </div>
                    )}

                    {showClearSelection && (
                        <button
                            type="button"
                            onClick={() => handleCardSelection([], null, null)}
                            className={`self-end text-sm font-medium transition-colors ${
                                selectedBucketAlias
                                    ? "text-slate-600 hover:text-slate-900"
                                    : "pointer-events-none invisible"
                            }`}
                        >
                            {clearSelectionLabel}
                        </button>
                    )}
                </div>
            </div>

            {isLoading && (
                <p className="mb-4 text-sm font-medium text-slate-500">
                    Loading due date report...
                </p>
            )}

            {reportError && !isLoading && (
                <div className="p-4 mb-12 text-sm text-red-700 bg-red-50 border border-red-200 rounded">
                    Unable to load the due date report. Please try again.
                </div>
            )}

            {!reportError && <div className="flex flex-row mb-12 overflow-x-auto">
                {dueDateReports.map((status, index) => {
                    const isServices = selectedItemLabel === "Services";
                    const totalCount = isServices
                        ? status.numberOfServices
                        : status.numberOfJobs;
                    const selectedRows = isServices ? status.services : status.jobs;
                    const dueTodayRows = isServices
                        ? status.dueTodayServices
                        : status.dueTodayJobs;
                    const footerLabel = isServices ? "SERVICES" : "STEPS";
                    const isBucketSelected =
                        selectedBucketAlias === status.alias ||
                        (selectedBucketAlias === "dueToday" &&
                            status.alias === "next7days");

                    return (
                        <div
                            key={`${status.alias}-${index}`}
                            className={`relative flex flex-col rounded-xl text-white shadow-gray-900/20 shadow-md w-full p-7 mx-12 ${
                                status.bgClass
                            } ${
                                isBucketSelected ? "ring-4 ring-inset ring-white/80" : ""
                            }`}
                        >
                            <div className="relative flex flex-col items-center overflow-hidden text-center text-gray-700">
                                <p className="block text-sm font-normal leading-normal text-white uppercase">
                                    {status.name}
                                </p>
                                <div className="flex justify-center gap-1">
                                    <span className="mt-2 text-2xl"></span>
                                    {renderReportCountButton(totalCount || 0, () =>
                                        handleCardSelection(
                                            selectedRows || [],
                                            status.name,
                                            status.alias,
                                        ),
                                    )}
                                </div>

                                <p className="mt-4 text-white text-md">
                                    {footerLabel}
                                </p>
                                {status.alias === "next7days" &&
                                    renderDueTodayButton(status.dueTodayCount ?? 0, () =>
                                        handleCardSelection(
                                            dueTodayRows || [],
                                            "Due today",
                                            "dueToday",
                                        ),
                                    )}
                            </div>
                        </div>
                    );
                })}
            </div>}
        </section>
    );
};

export const mapDueDateServiceRowForAssignments = (row) => ({
    ...row,
    clientServiceSubscriptionId:
        row.clientServiceSubscriptionId ?? row.id ?? row.subscriptionId ?? null,
    serviceId: row.serviceId ?? row.id ?? row.clientServiceSubscriptionId,
    client: row.client ?? row.clientName ?? "",
    service:
        row.service ??
        row.serviceName ??
        row.clientServiceSubscriptionServiceName ??
        "",
    assignedBy:
        row.assignedBy ??
        row.assignedByEmployee ??
        row.assignedByUser ??
        row.createdBy ??
        null,
    year: row.year ?? row.clientServiceSubscriptionYear ?? "",
    dueDate: row.dueDate ?? row.subscriptionEndDate ?? null,
    isNew:
        row.isNew ??
        isDateWithinLastSevenDays(
            row.subscriptionStartDate ?? row.assignedDate ?? row.createdDate,
        ),
    notes: getNotesValue(
        row,
        row.notes,
        row.note,
        row.clientServiceSubscriptionNotes,
        row.subscriptionNotes,
        row.clientServiceSubscription?.notes,
    ),
    dueDateColorClass:
        row.dueDateColorClass ||
        getDueDateStatusFromDate(row.dueDate ?? row.subscriptionEndDate)?.bgClass,
});

export const mapDueDateJobRowForAssignments = (row) => ({
    ...row,
    subscriptionId:
        row.subscriptionId ?? row.clientServiceSubscriptionId ?? row.serviceId,
    client: row.client ?? row.clientName ?? "",
    service:
        row.service ??
        row.clientServiceSubscriptionServiceName ??
        row.serviceName ??
        "",
    assignedBy:
        row.assignedBy ??
        row.assignedByEmployee ??
        row.assignedByUser ??
        row.createdBy ??
        null,
    serviceYear:
        row.serviceYear ?? row.clientServiceSubscriptionYear ?? row.year ?? "",
    assignedDate: row.assignedDate ?? row.createdDate ?? row.dateCreated ?? null,
    dueDate: row.dueDate ?? null,
    jobStatus: row.jobStatus ?? row.status ?? 0,
    isNew:
        row.isNew ??
        isDateWithinLastSevenDays(
            row.assignedDate ?? row.createdDate ?? row.dateCreated,
        ),
    notes: getNotesValue(
        row,
        row.notes,
        row.note,
        row.clientServiceSubscriptionTaskNotes,
        row.taskNotes,
        row.clientServiceSubscriptionTask?.notes,
    ),
});

export default DueDateSummarySection;
